import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/shared/components/Layout';
import { useSettingsStore } from '@/stores/settingsStore';
import { playSuccessSound } from '@/shared/lib/soundEffects';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';
import { useNavigate } from 'react-router-dom';
import { api } from '@/shared/lib/api';
import { THEMES, ThemeName } from '@2enbota/shared';
import { captureError } from '@/shared/lib/monitoring';
import { SessionSizeSlider } from '@/shared/components/SessionSizeSlider';

const THEME_LABELS: Record<ThemeName, { label: string; emoji: string }> = {
  'default':          { label: 'Светлая',          emoji: '☀️' },
  'modern-dark':      { label: 'Тёмная',            emoji: '🌑' },
  'blue':             { label: 'Синяя',             emoji: '💙' },
  'green':            { label: 'Зелёная',           emoji: '💚' },
  'twilight':         { label: 'Сумерки',           emoji: '🌆' },
  'game':             { label: 'Игровая',           emoji: '🎮' },
  'modern-gradient':  { label: 'Градиент',          emoji: '🌈' },
  'cosmos':           { label: 'Космос',            emoji: '🌌' },
};


const TIMEZONE_OPTIONS = [
  { value: 'Europe/Kaliningrad', label: 'UTC+2 Калининград' },
  { value: 'Europe/Moscow',      label: 'UTC+3 Москва, СПб' },
  { value: 'Europe/Samara',      label: 'UTC+4 Самара, Саратов' },
  { value: 'Asia/Yekaterinburg', label: 'UTC+5 Екатеринбург, Уфа' },
  { value: 'Asia/Omsk',          label: 'UTC+6 Омск' },
  { value: 'Asia/Novosibirsk',   label: 'UTC+7 Новосибирск, Томск' },
  { value: 'Asia/Krasnoyarsk',   label: 'UTC+7 Красноярск' },
  { value: 'Asia/Irkutsk',       label: 'UTC+8 Иркутск' },
  { value: 'Asia/Yakutsk',       label: 'UTC+9 Якутск' },
  { value: 'Asia/Vladivostok',   label: 'UTC+10 Владивосток' },
  { value: 'Asia/Magadan',       label: 'UTC+11 Магадан' },
  { value: 'Asia/Kamchatka',     label: 'UTC+12 Камчатка' },
  { value: 'Asia/Tashkent',      label: 'UTC+5 Ташкент' },
  { value: 'Asia/Almaty',        label: 'UTC+5 Алматы' },
  { value: 'Asia/Tbilisi',       label: 'UTC+4 Тбилиси' },
  { value: 'Asia/Yerevan',       label: 'UTC+4 Ереван' },
  { value: 'Europe/Kyiv',        label: 'UTC+2 / UTC+3 Киев' },
  { value: 'Europe/Minsk',       label: 'UTC+3 Минск' },
  { value: 'Europe/Berlin',      label: 'UTC+1 / UTC+2 Берлин' },
  { value: 'Europe/London',      label: 'UTC+0 / UTC+1 Лондон' },
  { value: 'America/New_York',   label: 'UTC-5 / UTC-4 Нью-Йорк' },
  { value: 'UTC',                label: 'UTC (По Гринвичу)' },
];

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: 48,
        height: 28,
        minHeight: 'unset',
        borderRadius: 14,
        border: 'none',
        background: value ? 'var(--theme-primary)' : 'var(--theme-border)',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: value ? 22 : 3,
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: 'white',
        transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }} />
    </button>
  );
}

function SettingRow({ label, description, children }: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 0',
      borderBottom: '1px solid var(--theme-border)',
      gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: 'var(--theme-text)', fontWeight: 500 }}>{label}</div>
        {description && (
          <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2 }}>{description}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--theme-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: 1.2,
        marginBottom: 8,
        textAlign: 'center',
      }}>
        {title}
      </div>
      <div style={{
        background: 'var(--theme-bg-card)',
        border: '1px solid var(--theme-border)',
        borderRadius: 'var(--theme-border-radius)',
        padding: '0 16px',
      }}>
        {children}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { impact } = useHapticFeedback();
  const [isPlayingTest, setIsPlayingTest] = useState(false);
  const testTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSoundTriggerRef = useRef<number>(0);

  const handleTestSound = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lastSoundTriggerRef.current < 350) return;
    lastSoundTriggerRef.current = now;

    try {
      impact('medium');
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(25);
      }
    } catch {}

    setIsPlayingTest(true);
    playSuccessSound(3, true);

    if (testTimeoutRef.current) clearTimeout(testTimeoutRef.current);
    testTimeoutRef.current = setTimeout(() => {
      setIsPlayingTest(false);
    }, 600);
  };

  useEffect(() => {
    return () => {
      if (testTimeoutRef.current) clearTimeout(testTimeoutRef.current);
    };
  }, []);

  const {
    theme, ttsEnabled, hapticEnabled, soundEffectsEnabled, autoAdvance, showAiModel, cardNavHotkeys, adminEditEnabled,
    setTheme, setTtsEnabled, setHapticEnabled, setSoundEffectsEnabled, setAutoAdvance, setShowAiModel, setCardNavHotkeys, setAdminEditEnabled
  } = useSettingsStore();
  const [inputLives, setInputLives] = useState(3);
  const [mcReps, setMcReps] = useState(1);
  const [mcBatchSize, setMcBatchSize] = useState(25);
  const [inputSessionSize, setInputSessionSize] = useState(25);
  const [srsSessionSize, setSrsSessionSize] = useState(25);
  const [dailyGoal, setDailyGoal] = useState(20);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [timezone, setTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow';
    } catch {
      return 'Europe/Moscow';
    }
  });

  const isLoadedRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Загружаем актуальные настройки с бэкенда при монтировании
  useEffect(() => {
    api.get<any>('/users/settings').then(s => {
      if (!s) return;
      if (s.input_lives != null) setInputLives(s.input_lives);
      if (s.mc_repetitions_per_direction != null) setMcReps(s.mc_repetitions_per_direction);
      if (s.mc_batch_size != null) setMcBatchSize(s.mc_batch_size);
      if (s.input_session_size != null) setInputSessionSize(s.input_session_size);
      if (s.srs_session_size != null) setSrsSessionSize(s.srs_session_size);
      if (s.daily_goal != null) setDailyGoal(s.daily_goal);
      if (s.theme != null) setTheme(s.theme);
      if (s.tts_enabled != null) setTtsEnabled(s.tts_enabled);
      if (s.haptic_enabled != null) setHapticEnabled(s.haptic_enabled);
      if (s.auto_advance != null) setAutoAdvance(s.auto_advance);
      if (s.notifications_enabled != null) setNotificationsEnabled(s.notifications_enabled);
      if (s.timezone) setTimezone(s.timezone);
      // Помечаем как загруженное — теперь изменения будут сохраняться
      isLoadedRef.current = true;
    }).catch((err) => {
      captureError(err, { context: 'SettingsPage.loadSettings' });
      isLoadedRef.current = true;
    });
  }, []);

  // Автосохранение: дебаунс 600ms после любого изменения
  useEffect(() => {
    if (!isLoadedRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      api.put('/users/settings', {
        theme,
        tts_enabled: ttsEnabled,
        haptic_enabled: hapticEnabled,
        input_lives: inputLives,
        mc_repetitions_per_direction: mcReps,
        daily_goal: dailyGoal,
        study_direction_lock: 'both',
        auto_advance: autoAdvance,
        mc_batch_size: mcBatchSize,
        input_session_size: inputSessionSize,
        srs_session_size: srsSessionSize,
        notifications_enabled: notificationsEnabled,
        timezone,
      }).catch((err) => captureError(err, { context: 'SettingsPage.autoSave' }));
    }, 600);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [theme, ttsEnabled, hapticEnabled, autoAdvance, inputLives, mcReps, mcBatchSize, inputSessionSize, srsSessionSize, dailyGoal, notificationsEnabled, timezone]);

  return (
    <Layout>
      <div style={{ padding: '20px 16px', paddingBottom: 40 }}>
        {/* Шапка с кнопкой назад */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 20,
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 22,
              color: 'var(--theme-text)',
              padding: '0 8px 0 0',
              lineHeight: 1,
            }}
          >
            ←
          </button>
          <h1 style={{ flex: 1, margin: 0, fontSize: 22, color: 'var(--theme-text)', textAlign: 'center' }}>
            Настройки
          </h1>
          {/* Балансировочный элемент */}
          <div style={{ width: 30 }} />
        </div>

        {/* Тема */}
        <Section title="Внешний вид">
          <div style={{ padding: '16px 0' }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--theme-text)', marginBottom: 12, textAlign: 'center' }}>
              Тема оформления
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {THEMES.map((t) => {
                const { label, emoji } = THEME_LABELS[t];
                const isActive = theme === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    style={{
                      padding: '10px 12px',
                      background: isActive ? 'var(--theme-primary)20' : 'var(--theme-bg-elevated)',
                      border: `1px solid ${isActive ? 'var(--theme-primary)' : 'var(--theme-border)'}`,
                      borderRadius: 10,
                      color: isActive ? 'var(--theme-primary)' : 'var(--theme-text)',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        {/* Учёба */}
        <Section title="Учёба">

          <SettingRow label="Жизней в Input режиме" description="Количество попыток на каждое слово">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setInputLives(Math.max(1, inputLives - 1))}
                style={{ background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'var(--theme-text)', width: 32, height: 32, cursor: 'pointer', fontSize: 18, padding: 0 }}
              >−</button>
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-primary)', minWidth: 24, textAlign: 'center' }}>{inputLives}</span>
              <button
                onClick={() => setInputLives(Math.min(10, inputLives + 1))}
                style={{ background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'var(--theme-text)', width: 32, height: 32, cursor: 'pointer', fontSize: 18, padding: 0 }}
              >+</button>
            </div>
          </SettingRow>

          <SettingRow label="Повторений в MC режиме" description="На каждое направление">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setMcReps(Math.max(1, mcReps - 1))}
                style={{ background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'var(--theme-text)', width: 32, height: 32, cursor: 'pointer', fontSize: 18, padding: 0 }}
              >−</button>
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-primary)', minWidth: 24, textAlign: 'center' }}>{mcReps}</span>
              <button
                onClick={() => setMcReps(Math.min(5, mcReps + 1))}
                style={{ background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'var(--theme-text)', width: 32, height: 32, cursor: 'pointer', fontSize: 18, padding: 0 }}
              >+</button>
            </div>
          </SettingRow>

          <SettingRow label="Дневная цель" description="Карточек в день">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setDailyGoal(Math.max(5, dailyGoal - 5))}
                style={{ background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'var(--theme-text)', width: 32, height: 32, cursor: 'pointer', fontSize: 18, padding: 0 }}
              >−</button>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--theme-primary)', minWidth: 32, textAlign: 'center' }}>{dailyGoal}</span>
              <button
                onClick={() => setDailyGoal(Math.min(200, dailyGoal + 5))}
                style={{ background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'var(--theme-text)', width: 32, height: 32, cursor: 'pointer', fontSize: 18, padding: 0 }}
              >+</button>
            </div>
          </SettingRow>

          <SessionSizeSlider
            label="Карточек за MC сессию"
            description="Сколько слов в одном заходе 'Изучение новых'"
            value={mcBatchSize}
            onChange={setMcBatchSize}
          />
          <SessionSizeSlider
            label="Карточек за Ввод"
            description="Сколько слов за одну сессию режима 'Ввод'"
            value={inputSessionSize}
            onChange={setInputSessionSize}
          />
          <SessionSizeSlider
            label="Карточек за Повторение"
            description="Сколько слов за одну сессию режима 'Повторение'"
            value={srsSessionSize}
            onChange={setSrsSessionSize}
          />

          <SettingRow label="Автопереход" description="После ответа автоматически переходить к следующей карточке">
            <Toggle value={autoAdvance} onChange={setAutoAdvance} />
          </SettingRow>
        </Section>

        {/* Звук и тактильный */}
        <Section title="Звук и вибрация">
          <SettingRow label="Озвучивание слов" description="Серверный gTTS для английских слов">
            <Toggle value={ttsEnabled} onChange={setTtsEnabled} />
          </SettingRow>
          <SettingRow label="Звуковые эффекты" description="Звуки правильных ответов, комбо и опечаток">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                onClick={handleTestSound}
                onTouchEnd={handleTestSound}
                aria-label="Проверить звук"
                style={{
                  background: isPlayingTest ? 'var(--theme-primary)' : 'var(--theme-bg-elevated)',
                  border: `1px solid ${isPlayingTest ? 'var(--theme-primary)' : 'var(--theme-border)'}`,
                  borderRadius: 10,
                  color: isPlayingTest ? '#ffffff' : 'var(--theme-primary)',
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  height: 38,
                  minWidth: 84,
                  transform: isPlayingTest ? 'scale(0.94)' : 'scale(1)',
                  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isPlayingTest ? '0 0 12px rgba(124, 77, 255, 0.45)' : 'none',
                  userSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                }}
              >
                <span style={{ fontSize: 15, lineHeight: 1 }}>{isPlayingTest ? '🎵' : '🔊'}</span>
                <span>{isPlayingTest ? 'Играет!' : 'Тест'}</span>
              </button>
              <Toggle value={soundEffectsEnabled !== false} onChange={setSoundEffectsEnabled} />
            </div>
          </SettingRow>
          <SettingRow label="Вибрация" description="Haptic feedback на правильные/неправильные ответы">
            <Toggle value={hapticEnabled} onChange={setHapticEnabled} />
          </SettingRow>
        </Section>

        {/* Уведомления */}
        <Section title="Уведомления">
          <SettingRow
            label="Напоминания в Telegram"
            description="Бот напомнит, когда накопятся слова для повторения"
          >
            <Toggle value={notificationsEnabled} onChange={setNotificationsEnabled} />
          </SettingRow>

          {notificationsEnabled && (
            <SettingRow
              label="Часовой пояс"
              description="Чтобы бот писал только днём (с 10:00 до 21:00)"
            >
              <select
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
                style={{
                  background: 'var(--theme-bg-elevated)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 8,
                  color: 'var(--theme-text)',
                  padding: '6px 10px',
                  fontSize: 13,
                  maxWidth: 170,
                }}
              >
                {!TIMEZONE_OPTIONS.some(tz => tz.value === timezone) && (
                  <option value={timezone}>{timezone}</option>
                )}
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </SettingRow>
          )}
        </Section>

        {/* Обучение */}
        <Section title="Обучение">
          <SettingRow
            label="Показать обучение заново"
            description="Сбросить подсказки для новичка и пройти инструкцию снова"
          >
            <button
              onClick={() => {
                localStorage.removeItem('2enbota_onboarding_done');
                alert('Обучение будет показано при следующем открытии приложения');
                navigate('/');
              }}
              style={{
                padding: '8px 16px',
                background: 'var(--theme-bg-elevated)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: 'var(--theme-text)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              🔄 Сбросить
            </button>
          </SettingRow>
        </Section>

        {/* Разработчик */}
        <Section title="Разработчик">
          <SettingRow label="Показывать AI-модель" description="Попап с именем модели после каждого AI-ответа">
            <Toggle value={showAiModel} onChange={setShowAiModel} />
          </SettingRow>
          <SettingRow label="Хоткеи для листания" description="Ctrl+Left / Ctrl+Right для навигации по карточкам">
            <Toggle value={cardNavHotkeys} onChange={setCardNavHotkeys} />
          </SettingRow>
          <SettingRow label="Кнопка редактирования слова" description="Иконка ✏️ на карточке в режиме обучения — править словарь на лету (только для админа)">
            <Toggle value={adminEditEnabled} onChange={setAdminEditEnabled} />
          </SettingRow>
        </Section>
      </div>
    </Layout>
  );
}
